import { useState, useEffect } from 'react';
import { Plus, UserCog, Trash2, Key, Shield, User, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    nome: '',
    password: '',
    role: 'user',
    cargo_id: '',
  });
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadUsuarios();
    loadCargos();
  }, []);

  const loadUsuarios = async () => {
    try {
      const { data } = await api.get('/usuarios');
      setUsuarios(data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
      console.error(error);
    }
  };

  const loadCargos = async () => {
    try {
      const { data } = await api.get('/cargos');
      setCargos(data.filter((c) => c.ativo !== false));
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        cargo_id: formData.cargo_id || null,
      };
      if (editingUser) {
        // edição — não envia password
        const { password: _pw, username: _un, ...updatePayload } = payload;
        await api.put(`/usuarios/${editingUser.id}`, updatePayload);
        toast.success('Usuário atualizado');
      } else {
        await api.post('/usuarios', payload);
        toast.success('Usuário criado com sucesso');
      }
      setIsDialogOpen(false);
      resetForm();
      loadUsuarios();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao salvar usuário';
      toast.error(message);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    try {
      await api.post(`/usuarios/${selectedUser.id}/reset-password`, { new_password: newPassword });
      toast.success(`Senha de ${selectedUser.nome} redefinida com sucesso`);
      setIsPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Erro ao redefinir senha');
    }
  };

  const handleDelete = async (usuario) => {
    if (usuario.id === currentUser?.id) {
      toast.error('Você não pode excluir seu próprio usuário');
      return;
    }
    
    if (window.confirm(`Tem certeza que deseja excluir o usuário "${usuario.nome}"?`)) {
      try {
        await api.delete(`/usuarios/${usuario.id}`);
        toast.success('Usuário excluído com sucesso');
        loadUsuarios();
      } catch (error) {
        toast.error('Erro ao excluir usuário');
      }
    }
  };

  const openPasswordDialog = (usuario) => {
    setSelectedUser(usuario);
    setNewPassword('');
    setIsPasswordDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ username: '', nome: '', password: '', role: 'user', cargo_id: '' });
    setEditingUser(null);
  };

  const openEdit = (usuario) => {
    setEditingUser(usuario);
    setFormData({
      username: usuario.username,
      nome: usuario.nome,
      password: '',
      role: usuario.role,
      cargo_id: usuario.cargo_id || '',
    });
    setIsDialogOpen(true);
  };

  return (
    <div className="p-8" data-testid="usuarios-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white uppercase tracking-tight mb-2">Usuários</h1>
          <p className="text-slate-500">Gerencie os usuários do sistema</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm" data-testid="add-usuario-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-carbon border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase">
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Nome Completo</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  required
                  data-testid="usuario-nome"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Usuário (login)</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
                  className="bg-obsidian border-white/10 text-white disabled:opacity-60"
                  required
                  disabled={!!editingUser}
                  data-testid="usuario-username"
                />
              </div>
              {!editingUser && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Senha</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                    required
                    minLength={4}
                    data-testid="usuario-password"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Nível de Acesso</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger className="bg-obsidian border-white/10 text-white" data-testid="usuario-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-carbon border-white/10 text-white">
                    <SelectItem value="user">Usuário</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Administrador tem acesso total, ignora cargo.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Cargo</Label>
                <Select
                  value={formData.cargo_id || '__none__'}
                  onValueChange={(value) => setFormData({ ...formData, cargo_id: value === '__none__' ? '' : value })}
                  disabled={formData.role === 'admin'}
                >
                  <SelectTrigger className="bg-obsidian border-white/10 text-white disabled:opacity-60" data-testid="usuario-cargo">
                    <SelectValue placeholder="Selecione um cargo (opcional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-carbon border-white/10 text-white">
                    <SelectItem value="__none__">Sem cargo (sem permissões)</SelectItem>
                    {cargos.map((cargo) => (
                      <SelectItem key={cargo.id} value={cargo.id}>
                        {cargo.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Define quais telas e ações o usuário poderá acessar.
                </p>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-slate-400">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="submit-usuario-btn">
                  {editingUser ? 'Salvar' : 'Criar Usuário'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase">Redefinir Senha</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-slate-400">
              Defina uma nova senha para <span className="text-white font-semibold">{selectedUser?.nome}</span>
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Nova Senha</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-obsidian border-white/10 text-white"
                required
                minLength={4}
                data-testid="new-password-input"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setIsPasswordDialogOpen(false)} className="text-slate-400">
                Cancelar
              </Button>
              <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="submit-password-btn">
                Redefinir Senha
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Users List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {usuarios.length === 0 ? (
          <div className="col-span-full bg-carbon border border-white/5 rounded-md p-12 text-center">
            <UserCog className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500">Nenhum usuário cadastrado</p>
          </div>
        ) : (
          usuarios.map((usuario) => (
            <div
              key={usuario.id}
              className="bg-carbon border border-white/5 rounded-md p-6 hover:border-white/20 transition-colors"
              data-testid={`usuario-card-${usuario.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    usuario.role === 'admin' ? 'bg-amber-500/20 text-amber-500' : 'bg-electric-blue/20 text-electric-blue'
                  }`}>
                    {usuario.role === 'admin' ? <Shield className="w-5 h-5" /> : <User className="w-5 h-5" />}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{usuario.nome}</h3>
                    <p className="text-sm text-slate-500">@{usuario.username}</p>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium uppercase tracking-wider ${
                  usuario.role === 'admin' 
                    ? 'bg-amber-500/10 text-amber-500' 
                    : 'bg-slate-500/10 text-slate-400'
                }`}>
                  {usuario.role === 'admin' ? 'Admin' : 'Usuário'}
                </span>
              </div>

              {usuario.role !== 'admin' && (
                <div className="mb-3 text-xs text-slate-400">
                  <span className="text-slate-500">Cargo:</span>{' '}
                  <span className="text-electric-blue font-medium">
                    {cargos.find((c) => c.id === usuario.cargo_id)?.nome || 'Sem cargo'}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-white/5 gap-2 flex-wrap">
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEdit(usuario)}
                    className="text-electric-blue hover:text-white hover:bg-electric-blue/10"
                    data-testid={`edit-usuario-${usuario.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openPasswordDialog(usuario)}
                    className="text-slate-300 hover:text-white hover:bg-white/5"
                    data-testid={`reset-password-${usuario.id}`}
                  >
                    <Key className="w-4 h-4 mr-1" />
                    Senha
                  </Button>
                </div>
                {usuario.id !== currentUser?.id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(usuario)}
                    className="text-red-500 hover:text-white hover:bg-red-500/10"
                    data-testid={`delete-usuario-${usuario.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Usuarios;
